import { Controller } from "@hotwired/stimulus"
import type { TicketGuardian } from "../types/global"

const WIDGET_SRC =
  "https://ddxooujlbmivk.cloudfront.net/widget/simple-widget/js/client-widget.min.js"

// Connects to data-controller="ticket-guardian"
//
// Replaces two inline <script> blocks in registrations/show.html.erb: the widget
// loader and a `tg('configure', {...})` call that interpolated an API key, a
// config flag, a JSON array and two record-scoped URLs straight into JavaScript.
// Interpolating into a <script> is the one context Rails cannot escape safely,
// so the config moves to data attributes instead.
//
// The old callbacks used jQuery ($.post / $.ajax), which is not loaded on this
// page -- opting in or out of insurance has therefore been broken. This restores
// it with fetch.
export default class extends Controller {
  static values = {
    apiKey: String,
    sandbox: Boolean,
    costs: Array,
    addInsuranceUrl: String,
    removeInsuranceUrl: String,
    totalSelector: { type: String, default: "#total_due" }
  }

  declare apiKeyValue: string
  declare sandboxValue: boolean
  declare costsValue: string[]
  declare addInsuranceUrlValue: string
  declare removeInsuranceUrlValue: string
  declare totalSelectorValue: string

  connect(): void {
    this.installWidget()
    this.configure()
  }

  // The iframe fires this once it has loaded; sendInfoToTGIframe is defined by
  // the widget script, which may not have arrived yet.
  iframeLoaded(): void {
    try {
      window.sendInfoToTGIframe?.()
    } catch {
      // Widget not ready -- it will send the info itself when it initialises.
    }
  }

  private installWidget(): void {
    if (window.tg) return

    // Command queue, so `tg('configure', ...)` below is replayed once the real
    // widget script loads.
    const queue = ((...args: unknown[]): void => {
      ;(queue.q = queue.q || []).push(args)
    }) as TicketGuardian
    queue.l = Number(new Date())
    window.tg = queue

    const script = document.createElement("script")
    script.async = true
    script.src = WIDGET_SRC
    document.head.appendChild(script)
  }

  private configure(): void {
    window.tg?.("configure", {
      apiKey: this.apiKeyValue,
      locale: "en_US",
      sandbox: this.sandboxValue,
      currency: "USD",
      costsOfItems: this.costsValue,
      optInCb: (): void => {
        const quote = window.tg?.get?.("quote")
        void this.replaceTotal(this.addInsuranceUrlValue, "POST",
                               new URLSearchParams({ quote: String(quote ?? "") }))
      },
      optOutCb: (): void => {
        void this.replaceTotal(this.removeInsuranceUrlValue, "DELETE")
      }
    })
  }

  private async replaceTotal(url: string, method: string, body?: URLSearchParams): Promise<void> {
    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""

    const response = await fetch(url, {
      method,
      headers: { "X-CSRF-Token": token, Accept: "text/html" },
      body
    })
    if (!response.ok) return

    const total = document.querySelector(this.totalSelectorValue)
    if (total) total.outerHTML = await response.text()
  }
}
